// interfaces
import { IQuoteCardResult } from "../../interfaces/results/IQuoteCardResult";
import { IQuoteGroupResultData } from "../../interfaces/IQuoteGroupResultData";
import { IQuoteResult } from "../../interfaces/results/IQuoteResult";

// types
import { IMapper } from "../../interfaces/IMapper";
import { ProductType } from "../../../common/interfaces/enums/ProductType";
import { SpecialOffer } from "../../enums/SpecialOffer";
import { IApiQuoteGroupCard } from "../../interfaces/api/group/results/IApiQuoteGroupCard";
import { ApiGroupCaseResult } from "../../types/api/ApiGroupCaseResult";
import EnumMapper from "./EnumMapper";
import { IProviderRating } from "../../interfaces/IProviderRating";
import { IApiProviderRating } from "../../interfaces/api/group/results/components/IApiProviderRating";
import LifeSettingMapper from "./settings/LifeSettingMapper";
import TraumaSettingMapper from "./settings/TraumaSettingMapper";
import HealthSettingMapper from "./settings/HealthSettingMapper";
import IncomeSettingMapper from "./settings/IncomeSettingMapper";
import MortgageSettingMapper from "./settings/MortgageSettingMapper";
import { QuoteSettingContainerType } from "../../../common/interfaces/settings/QuoteSettingContainerType";
import WaiverOfPremiumMapper from "./settings/WaiverOfPremiumMapper";
import ResultMapper from "./ResultMapper";
import { ApiQuoteConfiguration } from "../../types/api/ApiQuoteConfiguration";
import QuoteErrorType from "../../enums/QuoteErrorType";
import { DiscountType } from "../../../common/interfaces/enums/DiscountType";
import { isBlockingError } from "./MapperHelpers";

export function retrieveGroupMapper(
    productType: ProductType
): IMapper<QuoteSettingContainerType> {
    switch (productType) {
        case ProductType.Life:
            return LifeSettingMapper;
        case ProductType.Health:
            return HealthSettingMapper;
        case ProductType.Trauma:
            return TraumaSettingMapper;
        case ProductType.Income:
            return IncomeSettingMapper;
        case ProductType.Mortgage:
            return MortgageSettingMapper;
        case ProductType.WaiverOfPremium:
            return WaiverOfPremiumMapper;
    }
}

export function mapRemoteSettingToLocal(
    setting: ApiQuoteConfiguration
): QuoteSettingContainerType {
    const productType = EnumMapper.Product.remoteToLocal(setting.productType);

    const mapper = retrieveGroupMapper(productType);

    return mapper.remoteToLocal(setting);
}

export const mapServerProductRatingToLocal = (
    ratings: IApiProviderRating
): IProviderRating => {
    return {
        productRatings:
            ratings?.productRatings?.map((item) => {
                return {
                    percentRating: item.percentRating,
                    productId: item.productId,
                    productType: EnumMapper.Product.remoteToLocal(item.product), //item.product.toLowerCase() as ProductType,
                    starRating: item.starRating
                };
            }) || [],
        providerId: ratings?.providerId,
        sortByRating: ratings?.sortByRating,
        totalPercentRating: ratings?.totalPercentRating,
        totalStarRating: ratings?.totalStarRating
    };
};

export const mapServerQuoteCardToLocal = (
    input: IApiQuoteGroupCard[]
): IQuoteCardResult[] => {
    const res: IQuoteCardResult[] = input.map((card) => {
        const result = ResultMapper.remoteToLocal(card.result);

        const products: IQuoteCardResult["productInfo"] = {};

        Object.values(card.quoteProducts).forEach((prod) => {
            const productType = EnumMapper.Product.remoteToLocal(prod.productType);
            products[productType] = {
                productId: prod.productId,
                productName: prod.productName,
                productType,
                policyFeatures:
                    prod?.policyFeatures?.map((i) => ({
                        id: i.id,
                        sectionId: i.sectionId,
                        detail: i.detail,
                        detailMore: i.detailMore,
                        detailShowMore: i.detailShowMore,
                        sectionName: i.sectionName,
                        orderBy: i.orderBy,
                        tooltip: i.tooltip,
                        isDifference: i.isDifference
                    })) || [],
                documents: {
                    policy: {
                        documentUrl: prod.policyDocument?.fileUrl,
                        thumbnailUrl: prod.policyDocument?.thumbnailUrl
                    },
                    brochure: {
                        documentUrl: prod.policyBrochure?.fileUrl,
                        thumbnailUrl: prod.policyBrochure?.thumbnailUrl
                    }
                },
                sellingPoints:
                    prod.sellingPoints?.map((i) => ({
                        id: i.id,
                        text: i.description
                    })) || []
            };
        });

        const cardResult: IQuoteCardResult = {
            cardKey: 0,
            result,
            applicationSource: card.applicationSource,
            totalCashback: card.cashback,
            state: {
                isFavourited: card.isFavourited || false,
                isSelected: card.isSelected || false,
                isHighlighted: false,
                isOverMinimumPremium: !card.result.errors.some(
                    (e) =>
                        EnumMapper.QuoteErrorType.remoteToLocal(e.type) ===
                        QuoteErrorType.MinimumPremium
                ),
                hasMultiBenefitDiscountApplied: result.benefits.some((b) =>
                    b.discounts.some((d) => d.type === DiscountType.MultiPolicy)
                ),
                hasMultiBenefitDiscountOffer: card.hasMultiBenefitDiscountOffer,
                // Blocking errors are any error that prevent us from creating a quote,
                // such as validation or quote engine errors; these errors get the N/ A cards
                hasBlockingError: result.errors.some(isBlockingError)
            },
            ratings: {
                premium: {
                    orderBy: result.totalPremium,
                    title: "Price",
                    ratingInfo: result.totalPremium
                },
                customerService: {
                    orderBy: card.customerServiceRating,
                    title: "Customer Service",
                    ratingInfo: card.customerServiceRating
                },
                financialStrength: {
                    orderBy: card.financialStrengthRating?.ratingOrderBy,
                    title: "Financial Strength",
                    ratingInfo: {
                        ratingAgency: card.financialStrengthRating?.ratingAgency,
                        rating: card.financialStrengthRating?.rating,
                        ratingDescription:
                            card.financialStrengthRating?.ratingDescription,
                        ratingOrder: card.financialStrengthRating?.ratingOrder
                    }
                },
                provider: {
                    orderBy: card.providerRating?.sortByRating,
                    title: "Rating",
                    ratingInfo: mapServerProductRatingToLocal(card.providerRating)
                },
                providerPopularity: {
                    orderBy: card.providerPopularity?.numberOfClients,
                    title: "Popularity",
                    ratingInfo: card.providerPopularity?.numberOfClients
                }
            },
            
            providerAddons:
                card.providerAddOns?.map((i) => ({
                    id: i.providerAddOnId as number,
                    systemName: i.systemName as string,
                    providerId: i.providerId,
                    addOnName: i.addOnName,
                    fee: i.fee as number,
                    discountPercentage: i.discountPercentage as number,
                    isSelectedByDefault: i.isSelectedByDefault as boolean,
                    quoteAmountInfoText: i.quoteAmountInfoText,
                    tipText: i.tipText,
                    breakdownList: i.breakdownList
                })) || [],
            specialOffers:
                card.specialOffers?.map((i) => ({
                    specialOfferName: i.specialOfferName as SpecialOffer
                })) || [],
            productInfo: products
        };

        return cardResult;
    });

    return res;
};

export const mapServerApplyResultToLocalResult = (
    res: ApiGroupCaseResult
): IQuoteGroupResultData => {
    const mapper = retrieveGroupMapper(
        EnumMapper.Product.remoteToLocal(res.quoteConfiguration.productType)
    );

    const quoteResult: IQuoteResult = ResultMapper.remoteToLocal(res.quoteResult);

    return {
        productId: res.productId,
        quoteResults: quoteResult,
        quoteSetting: mapper.remoteToLocal(res.quoteConfiguration)
    };
};
